import React from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text
} from '@chakra-ui/react'
import startCase from 'lodash/startCase'
import { formatMoney } from 'lib/numbers'
import { LedgerItemWithBalance } from 'lib/ledger'

type BuildiumResyncModalProps = {
  errorMessage?: string
  isLoading: boolean
  isOpen: boolean
  ledgerItem?: LedgerItemWithBalance
  onClose: () => void
  onConfirm: () => void | Promise<void>
}

const getLedgerItemLabel = (ledgerItem: LedgerItemWithBalance) => {
  if (ledgerItem.type === 'Charge') return 'charge'
  if (ledgerItem.type === 'Credit') return 'credit'
  return 'ledger row'
}

const BuildiumResyncModal: React.FC<BuildiumResyncModalProps> = ({
  errorMessage,
  isLoading,
  isOpen,
  ledgerItem,
  onClose,
  onConfirm
}) => {
  if (!ledgerItem) {
    return null
  }

  const ledgerItemLabel = getLedgerItemLabel(ledgerItem)
  const details = [
    { label: 'Type', value: startCase(ledgerItem.type) },
    { label: 'Category', value: startCase(ledgerItem.liabilityLabel) },
    { label: 'Date', value: ledgerItem.date },
    { label: 'Amount', value: formatMoney(ledgerItem.amount, 2, '$') }
  ]

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose} size='md'>
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader>Attempt to resync {ledgerItemLabel} to Buildium</ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        <ModalBody>
          <Text color='gray.700' mb={3}>
            There is no Buildium transaction linked to this {ledgerItemLabel}, so it may not have
            been posted correctly.
          </Text>
          <Text color='gray.700' mb={4}>
            Clicking this button will attempt to match an existing Buildium transaction to this row.
            If none is found, a new {ledgerItemLabel} will be posted to Buildium and linked to this
            row.
          </Text>
          {errorMessage && (
            <Alert alignItems='flex-start' borderRadius='sm' mb={4} status='error'>
              <AlertIcon />
              <Text mb={0} overflowWrap='anywhere'>
                {errorMessage}
              </Text>
            </Alert>
          )}
          <SimpleGrid
            bg='gray.50'
            border='1px solid'
            borderColor='gray.200'
            borderRadius='sm'
            columns={2}
            p={4}
            spacing={4}
          >
            {details.map((detail) => (
              <Box key={detail.label}>
                <Text color='gray.500' fontSize='xs' fontWeight='semibold' mb={1}>
                  {detail.label}
                </Text>
                <Text fontSize='sm' mb={0}>
                  {detail.value}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} variant='ghost' onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button colorScheme='teal' onClick={onConfirm} isLoading={isLoading}>
            Sync {ledgerItemLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default BuildiumResyncModal
